/**
 * CJK Strong 플러그인
 * marked의 GFM 모드에서 한글 등 CJK 문자가 단어 경계로 인식되지 않아
 * **텍스트(괄호)**한글, **[별지 1]**한글 패턴에서 strong 파싱이 실패하는 문제를 해결합니다.
 */

import type { Tokens, TokenizerThis } from 'marked';
import type { MarkdownPlugin } from '../types';

export const cjkStrongPlugin: MarkdownPlugin = {
	name: 'cjkStrong',
	extensions: [
		{
			name: 'cjkStrong',
			level: 'inline',
			// 높은 우선순위 설정 - 인라인 코드(백틱)보다 먼저 처리
			// marked 기본값은 0이며, 높을수록 먼저 실행됨
			priority: 1,
			start(src: string) {
				const asteriskIndex = src.indexOf('**');
				const underscoreIndex = src.indexOf('__');

				if (asteriskIndex === -1) return underscoreIndex;
				if (underscoreIndex === -1) return asteriskIndex;

				return Math.min(asteriskIndex, underscoreIndex);
			},
			tokenizer(this: TokenizerThis, src: string) {
				const isEscapedDelimiter = (text: string, delimiterIndex: number): boolean => {
					let backslashCount = 0;
					for (let i = delimiterIndex - 1; i >= 0 && text[i] === '\\'; i -= 1) {
						backslashCount += 1;
					}
					return backslashCount % 2 === 1;
				};

				const createStrongToken = (delimiter: '**' | '__') => {
					const delimiterLen = delimiter.length;
					let searchIndex = delimiterLen;

					while (searchIndex < src.length) {
						const endIndex = src.indexOf(delimiter, searchIndex);
						if (endIndex === -1) return undefined;

						if (!isEscapedDelimiter(src, endIndex)) {
							const innerText = src.slice(delimiterLen, endIndex);
							if (!innerText || innerText.trim() === '') return undefined;

							return {
								type: 'strong',
								raw: src.slice(0, endIndex + delimiterLen),
								text: innerText,
								tokens: this.lexer.inlineTokens(innerText),
							} as Tokens.Strong;
						}

						searchIndex = endIndex + 1;
					}

					return undefined;
				};

				if (src.startsWith('**')) return createStrongToken('**');
				if (src.startsWith('__')) return createStrongToken('__');

				return undefined;
			},
		},
	] as unknown as MarkdownPlugin['extensions'],
};
